import { useInfiniteQuery } from '@tanstack/react-query';
import { listLeadsV2 } from 'services/lead/leadServices';
import { queryKeys } from '../../config/queryClient';

/**
 * Infinite query hook for a single column - SINGLE CACHE ARCHITECTURE
 *
 * This hook manages ALL pages for a column in one infinite query cache.
 * Page 1 is pre-populated by useInitialBoardLoad, subsequent pages are fetched on scroll.
 *
 * Benefits:
 * - Single cache per column (no dual-cache complexity)
 * - All data in one place
 * - Simpler mutations (only update this cache)
 * - Built-in pagination state management
 *
 * @param {string} columnId - The column/stage ID
 * @param {Object} filters - Filter parameters (searchKeyword, practiceArea, assignedTo, sortType)
 * @returns {Object} - Query result with data, fetchNextPage, hasNextPage, etc.
 */
export const useColumnInfiniteQuery = (columnId, filters = {}) => {
  return useInfiniteQuery({
    queryKey: [...queryKeys.leads.column(columnId), filters],

    queryFn: async ({ pageParam = 2 }) => {
      // Fetch page 2+ (page 1 pre-loaded by useInitialBoardLoad)
      const limit = pageParam === 1 ? 15 : 20; // Page 1 gets 15, others get 20

      // Build request payload
      const requestData = {
        lead_stage_id: columnId,
        page: pageParam,
      };

      // Add filters to body
      if (filters.practiceArea) {
        requestData.practice_area = filters.practiceArea;
      }
      if (filters.assignedTo && filters.assignedTo.length > 0) {
        requestData.assigned_to = filters.assignedTo;
      }
      if (filters.sortType) {
        requestData.sort_type = filters.sortType;
      }

      // Note: keyword is passed as a separate parameter to match API signature
      const response = await listLeadsV2({
        limit,
        data: requestData,
        keyword: filters.searchKeyword || false,
        excludeFromLoading: true,
      });

      return {
        items: response?.data?.[columnId]?.items || [],
        page: response?.data?.[columnId]?.page || pageParam,
        hasMore: response?.data?.[columnId]?.has_more || false,
        totalCount: response?.data?.[columnId]?.total_count || 0,
      };
    },

    // Get the next page parameter
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.page + 1;
    },

    // Get the previous page parameter (optional, for bi-directional scroll)
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page <= 1) return undefined;
      return firstPage.page - 1;
    },

    initialData: undefined,

    // Query is enabled by default - page 1 is pre-populated by useInitialBoardLoad
    // This allows fetchNextPage() to work correctly for pagination
    enabled: false,

    // Use global defaults for consistency with other queries
    // staleTime: inherited from global (2 minutes)
    // refetchOnWindowFocus: inherited from global (false)

    notifyOnChangeProps: 'all',
  });
};
